import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  BookOpen, ChevronDown, ChevronRight, Mic, Volume2,
  ArrowRight, CheckCircle, MessageCircle, Lightbulb, Send, LogIn
} from 'lucide-react';
import { EXERCISES } from '../data/comprehensionEcriteExercises';

function StepHeader({ number, title, icon: Icon, isOpen, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 py-3 text-left group"
      data-testid={`step-${number}-toggle`}>
      <div className="w-7 h-7 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center text-xs font-bold flex-shrink-0">
        {number}
      </div>
      <Icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
      <span className="text-sm font-bold text-[#1A2F4A] flex-1" style={{ fontFamily: "'Playfair Display', serif" }}>
        {title}
      </span>
      <ChevronDown className={`w-4 h-4 text-[#999] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
}

function ExerciseCard({ exercise, index }) {
  const [openSteps, setOpenSteps] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });
  const [revealed, setRevealed] = useState({});
  const [speaking, setSpeaking] = useState(false);
  const { user, token } = useAuth();

  const [answers, setAnswers] = useState({
    taskA: exercise.questions.map(() => ''),
    taskB: exercise.expression.map(() => ''),
    taskC: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (step) => setOpenSteps(prev => ({ ...prev, [step]: !prev[step] }));
  const revealAnswer = (idx) => setRevealed(prev => ({ ...prev, [idx]: true }));

  const updateTaskA = (idx, val) => setAnswers(prev => {
    const arr = [...prev.taskA]; arr[idx] = val; return { ...prev, taskA: arr };
  });
  const updateTaskB = (idx, val) => setAnswers(prev => {
    const arr = [...prev.taskB]; arr[idx] = val; return { ...prev, taskB: arr };
  });

  const hasContent = answers.taskA.some(a => a.trim()) || answers.taskB.some(a => a.trim()) || answers.taskC.trim();

  const handleSubmit = async () => {
    if (!token || !hasContent) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          exercise_id: exercise.id,
          exercise_title: exercise.title,
          level: exercise.level,
          answers: { taskA: answers.taskA, taskB: answers.taskB, taskC: answers.taskC },
        }),
      });
      if (res.ok) setSubmitted(true);
    } catch { /* silent */ }
    setSubmitting(false);
  };

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden" data-testid={`exercise-${exercise.id}`}>
      {/* Exercise Header */}
      <div className="bg-[#FAF7F0] px-6 py-5 border-b border-[#E8E4DC]">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider bg-[#D4AF37]/10 px-2 py-0.5 rounded-full">
                {exercise.level}
              </span>
              <span className="text-[10px] text-[#999] uppercase tracking-wider">{exercise.theme}</span>
            </div>
            <h3 className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {exercise.title}
            </h3>
          </div>
          <div className="w-9 h-9 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center text-sm font-bold">
            {index + 1}
          </div>
        </div>
      </div>

      <div className="px-6 py-2 divide-y divide-[#E8E4DC]">

        {/* Step 1: Avant de lire */}
        <div>
          <StepHeader number={1} title="Avant de lire" icon={Lightbulb} isOpen={openSteps[1]} onToggle={() => toggle(1)} />
          {openSteps[1] && (
            <div className="pb-4 pl-10" data-testid={`step-1-content-${exercise.id}`}>
              <p className="text-xs text-[#999] italic mb-3">Think about these questions before reading:</p>
              <ul className="space-y-2">
                {exercise.warmup.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A2F4A]">
                    <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Step 2: Texte */}
        <div>
          <StepHeader number={2} title="Texte" icon={BookOpen} isOpen={openSteps[2]} onToggle={() => toggle(2)} />
          {openSteps[2] && (
            <div className="pb-4 pl-10" data-testid={`step-2-content-${exercise.id}`}>
              <button
                className="flex items-center gap-1.5 text-xs font-semibold text-[#1A2F4A] border border-[#E8E4DC] rounded-full px-3 py-1.5 mb-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid={`listen-btn-${exercise.id}`}>
                <Volume2 className="w-3.5 h-3.5" />
                Écouter le texte
              </button>
              <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5">
                {exercise.text.split('\n\n').map((para, i) => (
                  <p key={i} className="text-sm text-[#1A2F4A] leading-relaxed mb-3 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 3: Questions de compréhension */}
        <div>
          <StepHeader number={3} title="Questions de compréhension" icon={CheckCircle} isOpen={openSteps[3]} onToggle={() => toggle(3)} />
          {openSteps[3] && (
            <div className="pb-4 pl-10" data-testid={`step-3-content-${exercise.id}`}>
              <div className="space-y-3">
                {exercise.questions.map((item, i) => (
                  <div key={i} className="bg-[#FAF7F0] rounded-lg p-4">
                    <p className="text-sm font-medium text-[#1A2F4A] mb-2">{item.q}</p>
                    {revealed[i] ? (
                      <p className="text-sm text-[#D4AF37] italic" data-testid={`answer-${exercise.id}-${i}`}>
                        {item.a}
                      </p>
                    ) : (
                      <button
                        onClick={() => revealAnswer(i)}
                        className="text-xs text-[#6B4E37] border border-[#E8E4DC] rounded-full px-3 py-1 hover:border-[#D4AF37] transition-colors"
                        data-testid={`reveal-${exercise.id}-${i}`}>
                        Voir la réponse
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 4: Expression personnelle */}
        <div>
          <StepHeader number={4} title="Expression personnelle" icon={MessageCircle} isOpen={openSteps[4]} onToggle={() => toggle(4)} />
          {openSteps[4] && (
            <div className="pb-4 pl-10" data-testid={`step-4-content-${exercise.id}`}>
              <p className="text-xs text-[#999] italic mb-3">Now connect what you've read to your own life:</p>
              <ul className="space-y-2.5">
                {exercise.expression.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A2F4A]">
                    <Mic className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Step 5: Parler maintenant */}
        <div>
          <StepHeader number={5} title="Parler maintenant" icon={Mic} isOpen={openSteps[5]} onToggle={() => toggle(5)} />
          {openSteps[5] && (
            <div className="pb-4 pl-10" data-testid={`step-5-content-${exercise.id}`}>
              <div className="bg-[#1A2F4A] rounded-xl p-5 text-center">
                <p className="text-white text-sm font-medium mb-4">
                  « {exercise.speaking} »
                </p>
                <button
                  onClick={() => setSpeaking(!speaking)}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                    speaking
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-[#D4AF37] text-[#1A2F4A] hover:bg-[#C9A961]'
                  }`}
                  data-testid={`speak-btn-${exercise.id}`}>
                  <Mic className="w-4 h-4" />
                  {speaking ? 'Arrêter' : 'Parler maintenant'}
                </button>
                {speaking && (
                  <p className="text-[#D4AF37] text-xs mt-3 animate-pulse">Recording in progress...</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Step 6: Submit for Teacher Review */}
        <div>
          <StepHeader number={6} title="Submit for Teacher Review" icon={Send} isOpen={openSteps[6]} onToggle={() => toggle(6)} />
          {openSteps[6] && (
            <div className="pb-5 pl-10" data-testid={`step-6-content-${exercise.id}`}>
              {!user ? (
                <div className="bg-[#FAF7F0] rounded-xl p-5 text-center">
                  <LogIn className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-sm text-[#1A2F4A] mb-3">Log in to submit your answers for teacher review</p>
                  <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] border border-[#D4AF37] rounded-full px-4 py-2 hover:bg-[#D4AF37] hover:text-white transition-colors" data-testid="login-to-submit">
                    Log in
                  </Link>
                </div>
              ) : submitted ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid={`submitted-${exercise.id}`}>
                  <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-800">Submitted for teacher review</p>
                  <p className="text-xs text-emerald-600 mt-1">Check your Dashboard for feedback</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-[#999] italic">Write your answers below, then submit for teacher correction.</p>
                  <div>
                    <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task A</p>
                    <div className="space-y-2">
                      {exercise.questions.map((item, i) => (
                        <div key={i}>
                          <p className="text-xs text-[#6B4E37] mb-1">{i + 1}. {item.q}</p>
                          <textarea value={answers.taskA[i]} onChange={(e) => updateTaskA(i, e.target.value)} placeholder="Votre réponse..." rows={2} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none resize-none" data-testid={`answer-input-a-${exercise.id}-${i}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task B</p>
                    <div className="space-y-2">
                      {exercise.expression.map((prompt, i) => (
                        <div key={i}>
                          <p className="text-xs text-[#6B4E37] mb-1">{prompt}</p>
                          <textarea value={answers.taskB[i]} onChange={(e) => updateTaskB(i, e.target.value)} placeholder="Votre réponse..." rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none resize-none" data-testid={`answer-input-b-${exercise.id}-${i}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task C</p>
                    <p className="text-xs text-[#6B4E37] mb-1">{exercise.speaking}</p>
                    <textarea value={answers.taskC} onChange={(e) => setAnswers(prev => ({ ...prev, taskC: e.target.value }))} placeholder="Développez votre argumentation..." rows={4} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none resize-none" data-testid={`answer-input-c-${exercise.id}`} />
                  </div>
                  <button onClick={handleSubmit} disabled={!hasContent || submitting} className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white py-3 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`submit-btn-${exercise.id}`}>
                    <Send className="w-4 h-4" />
                    {submitting ? 'Submitting...' : 'Submit for Teacher Review'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function ComprehensionEcrite() {
  const [activeLevel, setActiveLevel] = useState('A1');
  const exercisesRef = React.useRef(null);

  const handleLevelClick = (lvl) => {
    setActiveLevel(lvl);
    setTimeout(() => {
      exercisesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const filtered = EXERCISES.filter(ex => ex.level === activeLevel);

  return (
    <div className="min-h-screen bg-[#FAF7F0]">

      {/* Hero */}
      <section className="pt-20 pb-10">
        <div className="max-w-[700px] mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Link to="/speaking-lab" className="text-xs text-[#999] hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3 text-[#999]" />
            <span className="text-xs text-[#D4AF37] font-semibold">Comprendre</span>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="comprehension-title">
            Compréhension écrite
          </h1>
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="h-px w-12 bg-[#D4AF37]/50"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50"></div>
            <div className="h-px w-12 bg-[#D4AF37]/50"></div>
          </div>
          <p className="text-sm text-[#6B4E37] leading-relaxed">
            Read, understand, and speak. Each exercise guides you from comprehension to personal expression.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="pb-8">
        <div className="max-w-[650px] mx-auto px-8">
          <div className="flex items-center gap-4 justify-center text-xs text-[#999]">
            {['Avant de lire', 'Texte', 'Compréhension', 'Expression', 'Parler'].map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ArrowRight className="w-3 h-3 text-[#E8E4DC]" />}
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center text-[9px] font-bold">{i + 1}</span>
                  {s}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Level Filter */}
      <section className="pb-8">
        <div className="max-w-[700px] mx-auto px-8">
          <div className="flex items-center justify-center gap-2" data-testid="level-filter">
            {LEVELS.map((lvl) => {
              const hasContent = EXERCISES.some(ex => ex.level === lvl);
              const isActive = activeLevel === lvl;
              return (
                <button
                  key={lvl}
                  onClick={() => handleLevelClick(lvl)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white shadow-sm'
                      : hasContent
                        ? 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                        : 'bg-white/50 border border-[#F3F4F6] text-[#CCC] cursor-default'
                  }`}
                  data-testid={`level-${lvl}`}>
                  {lvl}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Exercises */}
      <section className="pb-20" ref={exercisesRef}>
        <div className="max-w-[700px] mx-auto px-8">
          {filtered.length > 0 ? (
            <div className="space-y-6">
              {filtered.map((ex, i) => (
                <ExerciseCard key={ex.id} exercise={ex} index={i} />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-[#E8E4DC] rounded-xl p-10 text-center" data-testid="no-exercises">
              <BookOpen className="w-8 h-8 text-[#E8E4DC] mx-auto mb-3" />
              <p className="text-[#1A2F4A] font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {activeLevel} exercises coming soon
              </p>
              <p className="text-sm text-[#6B4E37]">
                New reading exercises for this level are being prepared.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Back to Speaking Lab */}
      <section className="pb-16">
        <div className="max-w-[700px] mx-auto px-8 text-center">
          <Link
            to="/speaking-lab"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2F4A] border border-[#E8E4DC] px-6 py-2.5 rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            data-testid="back-to-lab">
            Back to Speaking Lab
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
